<template>
  <div class="investment-profile">
    <div class="profile-header">
      <div class="header-title">
        <h2>
          {{ $t("investment_profile_heading") }}
          <span :class="[ 'status-badge', form.isInvestmentProfileActive ? 'active' : 'inactive' ]">
            {{ form.isInvestmentProfileActive ? $t("active") : $t("inactive") }}
          </span>
        </h2>
        <feather-icon icon="HelpCircleIcon" size="18" id="investment-profile-help"/>
        <b-tooltip
            target="investment-profile-help"
            placement="top"
            custom-class="custom-tooltip"
        >
          {{ $t("investment_tooltip_profile") }}
        </b-tooltip>
      </div>

      <h6 v-if="userHasNotEnoughBalance" class="balance-warning">
        {{ $t("automated_investment_activation_note",{ min_budget:this.minBudget }) }}
      </h6>
    </div>

    <transition name="fade">
      <b-form v-if="loader" @submit.prevent="onSave" class="profile-form">
        <!-- Investment Limits -->
        <div class="section-card">
          <h5>{{ $t("investment_limits") }}</h5>
          <b-row>
            <b-col md="4">
              <b-form-group :label="$t('investment_profile_max_per_invoice')">
                <b-form-select
                    v-model="form.maxPerInvoice"
                    :options="[{ value: '', text: $t('select_max_per_invoice'), disabled: true }, ...maxPerNumericOptions]"
                    :class="{ 'is-invalid': validationErrors.maxPerInvoice }"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$t('investment_profile_max_per_seller')">
                <b-form-select
                    v-model="form.maxPerSeller"
                    :options="[{ value: '', text: $t('select_max_per_seller'), disabled: true }, ...maxPerNumericOptions]"
                    :class="{ 'is-invalid': validationErrors.maxPerSeller }"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$t('investment_profile_max_per_debtor')">
                <b-form-select
                    v-model="form.maxPerDebtor"
                    :options="[{ value: '', text: $t('select_max_per_debtor'), disabled: true }, ...maxPerNumericOptions]"
                    :class="{ 'is-invalid': validationErrors.maxPerDebtor }"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Eligibility Filters -->
        <div class="section-card">
          <h5>{{ $t("investment_profile_eligibility_filters") }}</h5>
          <b-row>
            <b-col md="4">
              <b-form-group :label="$t('investment_profile_min_rating')">
                <b-form-select
                    v-model="form.minRating"
                    :options="ratingOptions"
                    :class="{ 'is-invalid': validationErrors.minRating }"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$t('investment_profile_maturity')">
                <b-form-select
                    v-model="form.maturity"
                    :options="maturityOptions"
                    :class="{ 'is-invalid': validationErrors.maturity }"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$t('investment_profile_company_age')">
                <b-form-select v-model="form.companyAge" :options="companyAgeOptions" :class="{ 'is-invalid': validationErrors.companyAge }"/>
              </b-form-group>
            </b-col>
          </b-row>
        </div>

        <!-- Focus areas -->
        <div class="section-card" style="position:relative; z-index: 2">
          <h5>{{ $t("investment_profile_focus_areas") }}</h5>
          <b-row>

            <b-col md="4">
              <b-form-group>
                <template #label>
                  {{ $t('investment_profile_seller_countries') }}
                  <span style="color: red">*</span>
                </template>
                <multiselect
                    v-model="form.sellerCountries"
                    :options="countryOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :placeholder="$t('pick_countries')"
                    label="text"
                    track-by="value"
                    :class="{ 'is-invalid': validationErrors.sellerCountries }"
                />
              </b-form-group>
            </b-col>

            <b-col md="4" class="exclude-industries ">
              <b-form-group>
                <template #label>
                  {{ $t('investment_profile_exclude_industries') }}
                  <span>{{' ('+ $t('optional') +') '}}</span>
                  <span style="color: red">*</span>
                </template>
                <multiselect
                    v-model="form.excludeIndustries"
                    :options="industryOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :placeholder="$t('pick_industries')"
                    label="text"
                    track-by="value"
                />
              </b-form-group>
            </b-col>

            <b-col md="4">
              <b-form-group>
                <template #label>
                  {{ $t('investment_profile_currencies') }}
                  <span style="color: red">*</span>
                </template>

                <multiselect
                    v-model="form.currencies"
                    :options="currencyOptions"
                    :multiple="true"
                    :close-on-select="false"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :placeholder="$t('select_currency')"
                    label="text"
                    track-by="value"
                    :class="{ 'is-invalid': validationErrors.currencies }"
                />
              </b-form-group>
            </b-col>

          </b-row>
        </div>

        <!-- Factoring and Notifications -->
        <div class="section-card">
          <h5>{{ $t("investment_profile_preferences") }}</h5>
          <b-row>
            <b-col md="4">
              <b-form-group>
                <template #label>
                  {{ $t('investment_profile_factoring_type') }}
                  <span style="color: red">*</span>
                </template>
                <b-form-checkbox-group
                    v-model="form.factoringType"
                    :options="factoringTypeOptions"
                    :state="validationErrors.factoringType?!validationErrors.factoringType:null"
                />
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group :label="$t('investment_profile_partial_purchase')">
                <b-form-radio-group
                    v-model="form.partialPurchase"
                    :options="yesNoOptions"
                    name="partial-purchase"
                />
              </b-form-group>
            </b-col>
            <b-col md="4" class="d-flex align-items-center">
              <b-form-checkbox v-model="form.emailNotificationsEnabled">
                {{ $t("investment_profile_email_notifications_explanation") }}
              </b-form-checkbox>
            </b-col>
          </b-row>
        </div>

        <template>

          <b-card v-if="pdfUrl && !documentSigned">
            <iframe
                :src="pdfUrl"
                style="width: 100%; height: 1000px; border: none;"
            ></iframe>
            <div style="width: 100%; text-align: right; margin-top: 20px;" v-if="pdfUrl">
              <button
                  type="button"
                  class="btn btn-primary"
                  @click="handleButtonClick"
              >
               <span>
                  {{ $t('skr_btn') }}
                </span>
              </button>

              <p v-if="skribbleError" class="text-danger mt-2" style="text-align: right;">
                {{ $t(skribbleError) }}
              </p>

              <SkribblePopup
                  :show="showPopup"
                  @cancel="showPopup = false"
                  @continue="sendForSigning"
              />
            </div>
          </b-card>

          <div v-else-if="generating" class="col-12">
            <img src="/new-loader.svg" alt="loader"/>
          </div>
        </template>

        <!-- Action Buttons -->
        <div class="floating-actions">
          <b-button
              type="submit"
              variant="primary"
              class="btn-modern"
              :disabled="userHasNotEnoughBalance"
          >
            {{ $t("save") }}
          </b-button>

          <b-button
              v-if="form.isInvestmentProfileActive"
              variant="outline-danger"
              class="btn-modern-outline"
              @click="$bvModal.show('deactivate-account-modal')"
          >
            {{ $t("deactivate") }}
          </b-button>
        </div>

        <b-alert v-if="saveSuccess" variant="success" dismissible class="save-alert">
          {{ $t("your_work_has_been_saved") }}
        </b-alert>
      </b-form>

      <div v-else class="loader-wrapper">
        <img src="/new-loader.svg" alt="Loading..."/>
      </div>
    </transition>

    <b-modal
        id="deactivate-account-modal"
        centered
        hide-footer
        :title="$t('deactivate_account')"
    >
      <b-card-text class="text-left">
        {{ $t('deactivate_account_confirmation') }}
      </b-card-text>

      <b-row>
        <b-col class="text-center">
          <b-button variant="secondary" class="mr-2" @click="$bvModal.hide('deactivate-account-modal')">
            {{ $t("no") }}
          </b-button>
          <b-button variant="danger" @click="deactivateAccount">
            {{ $t("yes") }}
          </b-button>
        </b-col>
      </b-row>
    </b-modal>

    <span style="color: red">* </span> <span>{{ $t('multi_select_hint') }}</span>
  </div>

</template>

<script>
import Multiselect from 'vue-multiselect'
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import {BCard} from "bootstrap-vue";

export default {
  name: 'InvestmentProfile',
  components: {BCard, Multiselect},
  data() {
    return {
      saveSuccess: false,
      loader: false,
      validationErrors: {},
      form: {
        maxPerInvoice: '',
        maxPerSeller: '',
        maxPerDebtor: '',
        partialPurchase: true,
        emailNotificationsEnabled: true,
        maturity: '',
        excludeIndustries: [],
        excludeDebtorIndustries: [],
        minRating: '',
        sellerCountries: [],
        currencies: [],
        factoringType: [
          "recourse",
          "non-recourse",
          "reverse",
        ],
        companyAge: '',
        sign_first: this.$store.state.auth.user?.first_name,
        sign_last:  this.$store.state.auth.user?.last_name,
      },
      countryOptions: [],
      currencyOptions: [],
      profileId: null,
      userHasNotEnoughBalance: false,
      isInvestmentProfileActive: false,
      pdfUrl: null,
      pdfPath: null,
      showPopup: false,
      skribbleError: '',
      generating: false,
      pollInterval: null,
      showDocument: false,
      documentSigned: false,
      form_type: 'investor_profile_agreement',
      minBudget:10000,
    }
  },
  computed: {
    maxPerNumericOptions() {
      return [
        {value: 5000, text: '5,000'},
        {value: 10000, text: '10,000'},
        {value: 20000, text: '20,000'},
        {value: 30000, text: '30,000'},
        {value: 40000, text: '40,000'},
        {value: 50000, text: '50,000'},
        {value: 60000, text: '60,000'},
        {value: 70000, text: '70,000'},
        {value: 80000, text: '80,000'},
        {value: 90000, text: '90,000'},
        {value: 100000, text: '100,000'},
        {value: 200000, text: '200,000'},
        {value: 300000, text: '300,000'},
        {value: 400000, text: '400,000'},
        {value: 500000, text: '500,000'},
      ];
    },
    ratingOptions() {
      return [
        {value: '', text: this.$t('select_max_rating'), disabled: true},
        {value: 'AAA', text: `AAA  ${this.$t('exceptionally_stable')}`},
        {value: 'AA+', text: `AA+  ${this.$t('excellent_payment_capacity')}`},
        {value: 'AA', text: `AA  ${this.$t('reliable_and_established')}`},
        {value: 'AA-', text: `AA-  ${this.$t('strong_profile')}`},
        {value: 'A+', text: `A+  ${this.$t('well_managed')}`},
        {value: 'A', text: `A  ${this.$t('stable_operations')}`},
        {value: 'A-', text: `A-  ${this.$t('solid_foundation')}`},
        {value: 'BBB+', text: `BBB+  ${this.$t('balanced_investment')}`},
        {value: 'BBB', text: `BBB  ${this.$t('sound_structure')}`},
        {value: 'BBB-', text: `BBB-  ${this.$t('conservative_growth')}`},
        {value: 'BB+', text: `BB+  ${this.$t('growth_oriented')}`},
        {value: 'BB', text: `BB  ${this.$t('emerging_transitioning')}`},
        {value: 'BB-', text: `BB-  ${this.$t('solid_prospect')}`}
      ];
    },
    yesNoOptions() {
      return [
        {value: true, text: this.$t('yes')},
        {value: false, text: this.$t('no')},
      ]
    },
    maturityOptions() {
      return [
        {value: '', text: this.$t('select_maturity'), disabled: true},
        {value: 30, text: this.$t('investment_profile_maturity_30')},
        {value: 60, text: this.$t('investment_profile_maturity_60')},
        {value: 90, text: this.$t('investment_profile_maturity_90')},
        {value: 91, text: this.$t('investment_profile_maturity_90_and_more')},
      ];
    },
    industryOptions() {
      return [
        {value: 'industry_construction', text: this.$t('industry_construction')},
        {value: 'industry_hospitality', text: this.$t('industry_hospitality')},
        {value: 'industry_events', text: this.$t('industry_events')},
        {value: 'industry_retail', text: this.$t('industry_retail')},
        {value: 'industry_arts_creative', text: this.$t('industry_arts_creative')},
        {value: 'industry_transport_logistics', text: this.$t('industry_transport_logistics')},
        {value: 'industry_b2c_debt_buyers', text: this.$t('industry_b2c_debt_buyers')},
        {value: 'industry_agriculture', text: this.$t('industry_agriculture')},
        {value: 'industry_fashion_textile', text: this.$t('industry_fashion_textile')},
        {value: 'industry_medical_technology', text: this.$t('industry_medical_technology')},
        {value: 'industry_healthcare_services', text: this.$t('industry_healthcare_services')},
        {value: 'industry_pharmaceuticals', text: this.$t('industry_pharmaceuticals')},
        {value: 'industry_legal_services', text: this.$t('industry_legal_services')},
        {value: 'industry_consulting_advisory', text: this.$t('industry_consulting_advisory')},
        {value: 'industry_education_training', text: this.$t('industry_education_training')},
        {value: 'industry_media_publishing', text: this.$t('industry_media_publishing')},
        {value: 'industry_financial_services', text: this.$t('industry_financial_services')},
        {value: 'industry_insurance', text: this.$t('industry_insurance')},
        {value: 'industry_oil_energy', text: this.$t('industry_oil_energy')},
        {value: 'industry_chemicals', text: this.$t('industry_chemicals')},
        {value: 'industry_automotive_trade', text: this.$t('industry_automotive_trade')},
        {value: 'industry_real_estate_agencies', text: this.$t('industry_real_estate_agencies')},
        {value: 'industry_temporary_employment', text: this.$t('industry_temporary_employment')},
        {value: 'industry_wholesale_trade_general', text: this.$t('industry_wholesale_trade_general')},
        {value: 'industry_it_system_integration', text: this.$t('industry_it_system_integration')},
        {value: 'industry_computer_programming', text: this.$t('industry_computer_programming')},
        {value: 'industry_e_commerce', text: this.$t('industry_e_commerce')},
        {value: 'industry_advertising_marketing', text: this.$t('industry_advertising_marketing')},
        {value: 'industry_crypto_blockchain', text: this.$t('industry_crypto_blockchain')},
        {value: 'industry_public_institutions', text: this.$t('industry_public_institutions')},
      ]
    },
    factoringTypeOptions() {
      return [
        {value: 'recourse', text: this.$t('Recourse')},
        {value: 'non-recourse', text: this.$t('Non-Recourse')},
        {value: 'reverse', text: this.$t('Reverse')},
      ]
    },
    companyAgeOptions() {
      return [
        {value: '', text: this.$t('select_company_age'), disabled: true},
        {value: '1', text: this.$t('company_age_less_1')},
        {value: '1-2', text: this.$t('company_age_1_2')},
        {value: '2', text: this.$t('company_age_more_2')},
      ]
    },
  },

  async created() {
    const user_id = this.$store.state.auth.user.id;
    this.fetchCountries();
    this.getMinBudget();
    await this.doesUserHaveEnoughBalance(user_id);

    await this.$http.get(`/investment-profile/${user_id}`)
        .then((res) => {
          this.loader = true
          const data = res.data.value;

          this.profileId = data.id;
          this.form.isInvestmentProfileActive = data.is_active;

          this.form.maxPerInvoice = data.max_per_invoice;
          this.form.maxPerSeller = data.max_per_seller;
          this.form.maxPerDebtor = data.max_per_debtor;
          this.form.partialPurchase = data.partial_purchase;
          this.form.emailNotificationsEnabled = data.email_notifications_enabled;
          this.form.maturity = data.maturity;
          this.form.excludeIndustries = data.exclude_industries || [];
          this.form.minRating = data.min_rating;

          this.form.sellerCountries = data.seller_countries.map(code => {
            return this.countryOptions.find(c => c.value === code) || {value: code, text: code}
          });

          this.form.currencies = data.currencies.map(code => {
            return this.currencyOptions.find(c => c.value === code) || {value: code, text: code}
          });

          this.form.factoringType = data.factoring_type || [];
          this.form.companyAge = data.company_age;
        })
        .catch((error) => {
          console.log(error);
        });
  },
  methods: {
    async onSave() {
      this.validationErrors = {};
      const errors = [];

      if (!this.form.maxPerInvoice) {
        errors.push(this.$t('investment_profile_max_per_invoice'));
        this.validationErrors.maxPerInvoice = true;
      }

      if (!this.form.maxPerSeller) {
        errors.push(this.$t('investment_profile_max_per_seller'));
        this.validationErrors.maxPerSeller = true;
      }

      if (!this.form.maxPerDebtor) {
        errors.push(this.$t('investment_profile_max_per_debtor'));
        this.validationErrors.maxPerDebtor = true;
      }

      if (this.form.partialPurchase === null) {
        errors.push(this.$t('investment_profile_partial_purchase'));
        this.validationErrors.partialPurchase = true;
      }

      if (!this.form.maturity) {
        errors.push(this.$t('investment_profile_maturity'));
        this.validationErrors.maturity = true;
      }

      if (!this.form.minRating) {
        errors.push(this.$t('investment_profile_min_rating'));
        this.validationErrors.minRating = true;
      }

      if (!this.form.currencies.length) {
        errors.push(this.$t('investment_profile_currencies'));
        this.validationErrors.currencies = true;
      }

      if (!this.form.factoringType.length) {
        errors.push(this.$t('investment_profile_factoring_type'));
        this.validationErrors.factoringType = true;
      }
      if (!this.form.companyAge.length) {
        errors.push(this.$t('investment_profile_company_age'));
        this.validationErrors.companyAge = true;
      }
      if (!this.form.sellerCountries.length) {
        errors.push(this.$t('investment_profile_seller_countries'));
        this.validationErrors.sellerCountries = true;
      }
      if (!this.form.factoringType.length) {
        errors.push(this.$t('investment_profile_factoring_type'));
        this.validationErrors.factoringType = true;
      }
      if (errors.length > 0) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("failed_to_save_auto_investment_profile"),
            icon: "AlertTriangleIcon",
            variant: "danger",
            text: this.$t("please_select") + ' ' + errors[0],
          },
        });
        return;
      }

      await this.generateInvestorAgreement();
    },

    fetchCountries() {
      this.$http.post('/countries').then(res => {
        const seen = new Set();
        let countries = res.data.value
            .filter(item => {
              if (seen.has(item.iso_code)) return false;
              seen.add(item.iso_code);
              return true;
            })
            .map(item => ({
              value: item.iso_code,
              text: item.name,
            }));

        // Only show Switzerland, Germany, and Austria for now
        const priority = ['CH', 'DE', 'AT'];
        countries = countries.filter(item => priority.includes(item.value));

        countries.sort((a, b) => {
          const aIndex = priority.indexOf(a.value);
          const bIndex = priority.indexOf(b.value);
          return aIndex - bIndex;
        });

        this.countryOptions = countries;

        // To show all countries instead, comment out the filter line above and uncomment below:
        /*
        countries.sort((a, b) => {
          const aIndex = priority.indexOf(a.value);
          const bIndex = priority.indexOf(b.value);
          if (aIndex === -1 && bIndex === -1) return a.text.localeCompare(b.text);
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });

        this.countryOptions = countries;
        */
      });
    },
    getMinBudget() {
      this.$http.post(`/settings/get/automated_investor_min_budget`).then((response) => {
        this.minBudget = response.data.value;
      })
    },

    doesUserHaveEnoughBalance(user_id) {
      return this.$http.get(`/investment-profile/get-enough-balance/${user_id}`).then(res => {
        let currencies = res.data.value || [];
        this.userHasNotEnoughBalance = currencies.length === 0;

        this.currencyOptions = currencies.map(currency => ({
          text: currency.code,
          value: currency.code
        }));
      });
    },

    handleButtonClick(event) {
      event.preventDefault();

      if (localStorage.getItem("hideSkribblePopup") === "true") {
        this.sendForSigning();
      } else {
        this.showPopup = true;
      }
    },

    async generateInvestorAgreement() {
      this.generating = true;
      this.documentSigned = false;
      const payload = this.preparePayload();
      const res = await this.$http.post('/skribble/generate-investor-agreement', payload);

      if (res) {
        this.pdfUrl = res.data.value.pdf_url;
        this.pdfPath = res.data.value.pdf_path ?? '';
        this.generating = false;
      }
    },

    async sendForSigning() {
      this.showPopup = false;
      this.skribbleError = null;

      const payload = {
        form: this.form,
        form_type: this.form_type,
        pdfPath: this.pdfPath
      };

      try {
        const res = await this.$http.post('/skribble/send-for-signing', payload);
        const signingUrl = res?.data?.value?.signing_url || null;

        if (!signingUrl) {
          this.error = "Internal server error";
        } else {
          this.signing_url = signingUrl;
          window.open(this.signing_url, "_blank");
          this.pollInterval = setInterval(this.checkSigningStatus, 10000);
        }
      } catch (err) {
        this.skribbleError = err.response?.data?.message || err.message || 'Something went wrong.'
      }
    },

    checkSigningStatus() {
      this.$http.post(`/skribble/check-status/${this.$store.state.auth.user.id}/${this.form_type}`).then((res) => {
        if (res.data?.value === 'SIGNED') {
          clearInterval(this.pollInterval)
          this.pollInterval = null
          this.documentSigned = true

          if (this.profileId) {
            this.updateInvestmentProfile();
          } else {
            this.saveInvestmentProfile();
          }
        }
      }).catch(err => {
        console.error('Error checking signing status', err)
      });
    },

    async saveInvestmentProfile() {
      try {
        const payload = this.preparePayload();
        const res = await this.$http.post('/investment-profile', payload);
        this.profileId = res.data.id;
        this.saveSuccess = true;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("profile_saved"),
            icon: "SuccessIcon",
            variant: "success",
          },
        });

        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } catch (err) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("failed_to_save_auto_investment_profile"),
            icon: "ErrorIcon",
            variant: "error",
          },
        });
        console.log(err)
      }
    },

    async updateInvestmentProfile() {
      try {
        const payload = this.preparePayload();
        const res = await this.$http.put(`/investment-profile/${this.profileId}`, payload);
        this.saveSuccess = true;
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("profile_updated"),
            icon: "SuccessIcon",
            variant: "success",
          },
        });

        setTimeout(() => {
          window.location.reload();
        }, 1000);

      } catch (err) {
        console.log(err)
      }
    },

    async deactivateAccount() {
      try {
        await this.$http.post(`/investment-profile/${this.profileId}/deactivate`);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t("account_deactivated"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
        this.form.isInvestmentProfileActive = false;
        this.$bvModal.hide('deactivate-account-modal');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } catch (err) {
        console.error(err);
      }
    },

    preparePayload() {
      const user = this.$store.state.auth.user;
      let language = this.$i18n.locale ?? 'de';
      this.form.lang = language;
      const investorAddress = [
        user.company?.address,
        user.company?.address_nr,
        user.company?.zip,
        user.company?.city,
        user.company?.country_name
      ].filter(Boolean).join(', ');

      const fallbackAddress = [
        user.address,
        user.address_nr,
        user.zip,
        user.city,
        user.country?.name
      ].filter(Boolean).join(', ');

      return {
        user_id: user.id,
        company_id: user.company?.id,
        is_active: this.form.isInvestmentProfileActive,
        max_per_invoice: this.form.maxPerInvoice,
        max_per_seller: this.form.maxPerSeller,
        max_per_debtor: this.form.maxPerDebtor,
        partial_purchase: this.form.partialPurchase,
        email_notifications_enabled: this.form.emailNotificationsEnabled,
        maturity: this.form.maturity,
        exclude_industries: this.form.excludeIndustries,
        min_rating: this.form.minRating,
        seller_countries: this.form.sellerCountries.map(c => c.value || c),
        currencies: this.form.currencies.map(c => c.value || c),
        factoring_type: this.form.factoringType,
        company_age: this.form.companyAge,
        investor_or_company_name: user.company?.name ?? user.first_name + ' ' + user.last_name,
        investor_address: investorAddress || fallbackAddress,
        investor_dob_uid: user.company?.uid ?? user.birthday,
        lang: language,
      };
    }
  },
}
</script>

<style scoped>
.b-form-group {
  margin-bottom: 1.25rem;
}

.exclude-industries .custom-checkbox {
  width: fit-content;
  min-width: 37%;
}

.custom-checkbox {
  z-index: 0;
}

.custom-checkbox .custom-control-label {
  cursor: pointer;
}

.multiselect .multiselect__tag {
  background-color: #204a38;
}


.is-invalid .custom-radio .custom-control-label::before,
.is-invalid .custom-checkbox .custom-control-label::before {
  border: 1px solid #dc3545;
}

.custom-control-inline {
  z-index: 0;
}

.investment-profile {
  background-color: #f8fafc;
  padding: 2rem;
  border-radius: 14px;
}

.profile-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.header-title {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.profile-header h2 {
  font-weight: 700;
  color: #182c25;
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.active {
  background: rgba(37, 180, 90, 0.15);
  color: #25b45a;
}

.status-badge.inactive {
  background: rgba(220, 53, 69, 0.15);
  color: #dc3545;
}

.balance-warning {
  color: #d93025;
  font-weight: 500;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;
}

.section-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.section-card h5 {
  font-weight: 600;
  margin-bottom: 1.25rem;
}

.multiselect,
.b-form-input,
.b-form-select {
  border-radius: 10px !important;
  border: 1px solid #ddd !important;
  box-shadow: none;
  transition: all 0.25s ease;
}
.multiselect.is-invalid {
  border: 1px solid red !important;
  border-radius: 7px !important;
}
.is-invalid custom-control-label::before {
  border: 1px solid red !important;
}


.floating-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  border-top: 1px solid #eee;
  padding-top: 1.25rem;
}

.btn-modern {
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.25s ease;
}

.btn-modern:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 180, 90, 0.25);
}

.btn-modern-outline {
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.25s ease;
}

.btn-modern-outline:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.25);
}

.save-alert {
  margin-top: 1rem;
  border-radius: 10px;
  font-weight: 500;
}

.loader-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.4s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
